import * as types from 'constants/ActionTypes';
import { PAYMENT_METHOD_OPTION_IDS, PAYMENT_METHODS_ACTION_ERROR_KIND } from 'commonComponents/paymentInformation/PaymentInformationConstants'; // #genai

// #genai Phase 2 — key a saved payment method row uniquely by its type + surrogate id.
export const paymentMethodKey = (type, id) => `${type}:${id}`;

// #genai Phase 2 — normalise the GET /v1/payment-methods array into a byId map.
// Guide §12.4 + top-of-file rule: the server flips the chosen row's isDefault true and clears
// EVERY sibling default (including across type) in one transaction — at most ONE isDefault=true
// row across the whole list. Defensive normalisation here enforces that invariant client-side:
// if the response ever contains >1 default (stale DB rows from before the server enforced this,
// concurrent tab races, etc.), we keep the flag ONLY on the highest-id winner and force the rest
// to false, so the UI never renders two DEFAULT badges. When the server is behaving correctly
// this is a no-op pass-through.
const toPaymentMethodsById = (paymentMethods = []) => {
  const valid = paymentMethods.filter(
    (method) => method && (method.type === 'credit-card' || method.type === 'ach'),
  );
  const defaultWinner = valid
    .filter((method) => method.isDefault)
    .sort((a, b) => b.id - a.id)[0] || null;
  return valid.reduce((acc, method) => {
    const isTheDefault = Boolean(
      defaultWinner && method.type === defaultWinner.type && method.id === defaultWinner.id,
    );
    acc[paymentMethodKey(method.type, method.id)] = { ...method, isDefault: isTheDefault };
    return acc;
  }, {});
};

const paymentInfoInit = {
  firstName: '',
  lastName: '',
  cardNumber: '',
  savedCardNumber: '',
  cardType: '',
  expirationDateMonth: '',
  expirationDateYear: '',
  accountType: '',
  routingNumber: '',
  accountNumber: '',
  accountNumberConfirm: '',
};

// #genai - Decoupled card slice for ACH-eligible toggle users.
const cardPaymentInfoInit = {
  firstName: '',
  lastName: '',
  cardNumber: '',
  savedCardNumber: '',
  cardType: '',
  expirationDateMonth: '',
  expirationDateYear: '',
};

// #genai - Decoupled ACH slice for ACH-eligible toggle users.
const achPaymentInfoInit = {
  firstName: '',
  lastName: '',
  accountType: '',
  routingNumber: '',
  accountNumber: '',
  accountNumberConfirm: '',
};

// #genai - Keep payment error keys aligned with editable form fields.
const paymentInfoErrorsInit = {
  ...paymentInfoInit,
};

const cardPaymentInfoErrorsInit = {
  ...cardPaymentInfoInit,
  ccConsent: '',
};

const achPaymentInfoErrorsInit = {
  ...achPaymentInfoInit,
  ccConsent: '',
};

const addressInfoInit = {
  emailAddress: '',
  company: '',
  country: '',
  phone: '',
  phoneExt: '',
  secondaryPhone: '',
  secondaryExt: '',
  address1: '',
  city: '',
  state: '',
  zip: '',
};

const initState = {
  isLoading: false,
  paymentInfo: {
    ...paymentInfoInit,
    isUpdated: false,
    isCardUpdated: false,
  },
  ccConsent: false,
  updatedCCConsent: false,
  achConsent: false, // #genai - separate consent flag for ACH payment method
  updatedACHConsent: false, // #genai - tracks whether ACH consent was changed in the current session
  paymentInfoErrors: { ...paymentInfoErrorsInit, ccConsent: '' },
  paymentInfoErrorsForBanner: { ...paymentInfoErrorsInit, ccConsent: '' },
  cardData: null,
  addressInfo: { ...addressInfoInit },
  isAddressInfoUpdated: false,
  addressInfoErrors: { ...addressInfoInit },
  addressInfoErrorsForBanner: { ...addressInfoInit },
  addressInfoErrorsForDisplay: { ...addressInfoInit },
  addressInfoErrorsForBannerDisplay: { ...addressInfoInit },
  submitInfo: {
    showMessage: false,
    errorMessage: null,
    errorCode: null, // #genai backend error code (ev-ecomm-orchestration-service) for banner mapping
    errorType: null, // #genai Phase 2 — backend errorType string (guide §4 branches on this)
    supportReference: null, // #genai Phase 2 — orphan 503 support-contact reference
  },
  achInfo: {}, // #genai
  isACHDefault: false, // #genai
  updatedACHDefault: false, // #genai
  cardPaymentInfo: { // #genai
    ...cardPaymentInfoInit,
    isUpdated: false,
    isCardUpdated: false,
  },
  achPaymentInfo: { // #genai
    ...achPaymentInfoInit,
    isUpdated: false,
  },
  cardPaymentInfoErrors: { ...cardPaymentInfoErrorsInit }, // #genai
  cardPaymentInfoErrorsForBanner: { ...cardPaymentInfoErrorsInit }, // #genai
  achPaymentInfoErrors: { ...achPaymentInfoErrorsInit }, // #genai
  achPaymentInfoErrorsForBanner: { ...achPaymentInfoErrorsInit }, // #genai
  selectedPaymentMethod: PAYMENT_METHOD_OPTION_IDS.Card, // #genai
  savedSelectedPaymentMethod: PAYMENT_METHOD_OPTION_IDS.Card, // #genai
  isSaveDisclaimerOpen: false, // #genai
  // #genai Phase 2 — unified saved payment methods list (ENABLE_MULTI_PAYMENT_METHODS).
  paymentMethods: {
    byId: {},
    loading: false,
    loaded: false,
    loadError: null, // when non-null, holds httpStatus + errorType from the load failure
    pendingActionKey: null, // `${type}:${id}` currently being set-default / removed
    actionError: null, // { errorType, kind: PAYMENT_METHODS_ACTION_ERROR_KIND, method }
    previousById: null, // rollback snapshot for optimistic updates
  },
  // #genai Phase 2 — cross-route "Payment method added" success banner payload.
  // Shape: { accountTypeLabel, accountSuffix, isDefault } | null. Set by AddAch / AddCard on
  // successful save; consumed once + cleared by SavedPaymentMethods on mount. Redux-based instead
  // of history.location.state so the banner reliably shows even when SavedPaymentMethods mounts
  // after the redirect (which is always the case — the parent conditional flips through null →
  // BillingEmptyState → SavedPaymentMethods as the payment-methods list loads).
  recentlyAddedMessage: null,
};

const billingInformationReducer = (state = initState, action = {}) => {
  // #genai NOSONAR (javascript:S1479 - 45 non-empty cases): This is a plain Redux reducer
  // where each case is exactly one action handler. The switch is the standard idiom for
  // reducers and every case is a distinct, minimal update shape (already close to what
  // Redux Toolkit's `createReducer` would emit). Splitting into a handler map would add an
  // indirection layer without changing behaviour, complexity per branch, or coverage — and
  // would risk a large unrelated diff across a shared Phase 1 / Phase 2 slice. Suppressed
  // with justification rather than refactored.
  switch (action.type) { // NOSONAR
    case types.LOAD_BILLING_INFO_START: return { ...state, isLoading: true };
    // #genai Reset all dirty flags after the saga seeds initial values via updateAchConsent /
    // updateCreditCardConsent etc. Without this, an address-only save would still treat the
    // active payment slice as dirty and trigger an unnecessary bank-account-info / bill-payer-info call.
    case types.LOAD_BILLING_INFO_COMPLETE: return {
      ...state,
      isLoading: false,
      updatedCCConsent: false,
      updatedACHConsent: false,
      updatedACHDefault: false,
    };
    case types.LOAD_BILLING_INFO_ERROR: return { ...state, isLoading: false };
    case types.BILLING_INFO_UPDATE_PAYMENT_INFO: {
      const isCardUpdated = action.paymentInfo.savedCardNumber === state.paymentInfo.savedCardNumber
      && state.paymentInfo.savedCardNumber !== action.paymentInfo.cardNumber;
      return {
        ...state,
        paymentInfo: {
          ...state.paymentInfo,
          ...action.paymentInfo,
          isCardUpdated,
        },
      };
    }
    case types.BILLING_INFO_UPDATE_CARD_CONSENT: return {
      ...state,
      ccConsent: action.ccConsent,
      updatedCCConsent: true,
    };
    case types.BILLING_INFO_UPDATE_ACH_CONSENT: return { // #genai
      ...state,
      achConsent: action.achConsent,
      updatedACHConsent: true,
    };
    case types.BILLING_INFO_LOAD_ACH_INFO: {
      const achInfo = action.achInfo || {};
      return {
        ...state,
        achInfo,
        isACHDefault: Boolean(achInfo.isACHDefault),
        updatedACHDefault: false,
      };
    }
    case types.BILLING_INFO_UPDATE_ACH_DEFAULT: return {
      ...state,
      isACHDefault: action.isACHDefault,
      updatedACHDefault: true,
    };
    case types.BILLING_INFO_UPDATE_CARD_PAYMENT_INFO: { // #genai
      const isCardUpdated = action.cardPaymentInfo.savedCardNumber === state.cardPaymentInfo.savedCardNumber
      && state.cardPaymentInfo.savedCardNumber !== action.cardPaymentInfo.cardNumber;
      return {
        ...state,
        cardPaymentInfo: {
          ...state.cardPaymentInfo,
          ...action.cardPaymentInfo,
          isCardUpdated,
        },
      };
    }
    case types.BILLING_INFO_UPDATE_ACH_PAYMENT_INFO: return { // #genai
      ...state,
      achPaymentInfo: {
        ...state.achPaymentInfo,
        ...action.achPaymentInfo,
      },
    };
    case types.BILLING_INFO_SET_SELECTED_PAYMENT_METHOD: return { // #genai
      ...state,
      selectedPaymentMethod: action.selectedPaymentMethod,
      ...(action.persistSaved ? { savedSelectedPaymentMethod: action.selectedPaymentMethod } : {}),
      // #genai Clear stale payment validation + status messages so errors/success from the
      // previous tab don't follow the user into the new tab. Address errors stay because
      // they're independent of the payment-method choice.
      paymentInfoErrors: { ...paymentInfoErrorsInit, ccConsent: '' },
      paymentInfoErrorsForBanner: { ...paymentInfoErrorsInit, ccConsent: '' },
      cardPaymentInfoErrors: { ...cardPaymentInfoErrorsInit },
      cardPaymentInfoErrorsForBanner: { ...cardPaymentInfoErrorsInit },
      achPaymentInfoErrors: { ...achPaymentInfoErrorsInit },
      achPaymentInfoErrorsForBanner: { ...achPaymentInfoErrorsInit },
      submitInfo: { ...initState.submitInfo },
    };
    case types.BILLING_INFO_OPEN_SAVE_DISCLAIMER: return { // #genai
      ...state,
      isSaveDisclaimerOpen: true,
    };
    case types.BILLING_INFO_CLOSE_SAVE_DISCLAIMER: return { // #genai
      ...state,
      isSaveDisclaimerOpen: false,
    };
    case types.BILLING_INFO_UPDATE_CARD_PAYMENT_INFO_ERRORS: return { // #genai
      ...state,
      cardPaymentInfoErrors: { ...action.cardPaymentInfoErrors },
    };
    case types.BILLING_INFO_UPDATE_ACH_PAYMENT_INFO_ERRORS: return { // #genai
      ...state,
      achPaymentInfoErrors: { ...action.achPaymentInfoErrors },
    };
    case types.BILLING_INFO_UPDATE_CARD_PAYMENT_INFO_ERRORS_FOR_BANNER: return { // #genai
      ...state,
      cardPaymentInfoErrorsForBanner: { ...action.cardPaymentInfoErrorsForBanner },
    };
    case types.BILLING_INFO_UPDATE_ACH_PAYMENT_INFO_ERRORS_FOR_BANNER: return { // #genai
      ...state,
      achPaymentInfoErrorsForBanner: { ...action.achPaymentInfoErrorsForBanner },
    };
    case types.BILLING_INFO_LOAD_ADDRESS_INFO: return { ...state, addressInfo: action.addressInfo };
    case types.BILLING_INFO_UPDATE_ADDRESS_INFO: {
      const addressInfoUpdated = { ...state.addressInfo };
      addressInfoUpdated[action.fieldId] = action.value || '';
      return {
        ...state,
        addressInfo: { ...addressInfoUpdated },
        isAddressInfoUpdated: true,
      };
    }
    case types.BILLING_INFO_UPDATE_ADDRESS_INFO_FIELDS: {
      return {
        ...state,
        addressInfo: { ...action.addressInfo },
        isAddressInfoUpdated: false,
      };
    }
    case types.BILLING_INFO_UPDATE_ADDRESS_INFO_ERRORS: {
      const addressInfoErrorsUpdated = { ...state.addressInfoErrors };
      addressInfoErrorsUpdated[action.fieldId] = action.fieldErrorValue;

      const addressInfoErrorsForBannerUpdated = { ...state.addressInfoErrorsForBanner };
      addressInfoErrorsForBannerUpdated[action.fieldId] = action.bannerErrorValue;
      return {
        ...state,
        addressInfoErrors: { ...addressInfoErrorsUpdated },
        addressInfoErrorsForBanner: { ...addressInfoErrorsForBannerUpdated },
      };
    }
    case types.BILLING_INFO_SHOW_ADDRESS_VALIDATION_ERRORS: return {
      ...state,
      addressInfoErrorsForDisplay: { ...state.addressInfoErrors },
      addressInfoErrorsForBannerDisplay: { ...state.addressInfoErrorsForBanner },
    };
    case types.BILLING_INFO__RESET_VALIDATION_ERRORS: return {
      ...state,
      paymentInfoErrors: { ...paymentInfoErrorsInit, ccConsent: '' },
      paymentInfoErrorsForBanner: { ...paymentInfoErrorsInit, ccConsent: '' },
      cardPaymentInfoErrors: { ...cardPaymentInfoErrorsInit }, // #genai
      cardPaymentInfoErrorsForBanner: { ...cardPaymentInfoErrorsInit }, // #genai
      achPaymentInfoErrors: { ...achPaymentInfoErrorsInit }, // #genai
      achPaymentInfoErrorsForBanner: { ...achPaymentInfoErrorsInit }, // #genai
      addressInfoErrors: { ...addressInfoInit },
      addressInfoErrorsForDisplay: { ...addressInfoInit },
      addressInfoErrorsForBanner: { ...addressInfoInit },
    };
    case types.BILLING_INFO_UPDATE_PAYMENT_INFO_ERRORS: return { ...state, paymentInfoErrors: { ...action.paymentInfoErrors } };
    case types.BILLING_INFO_UPDATE_PAYMENT_INFO_ERRORS_FOR_BANNER: return {
      ...state,
      paymentInfoErrorsForBanner: { ...action.paymentInfoErrorsForBanner },
    };
    // #genai Preserve recentlyAddedMessage across the page-load reset. The Add-ACH / Add-Card
    // routes set this field right before redirecting to /billing-information; if we returned
    // { ...initState } we would wipe it before SavedPaymentMethods gets a chance to render the
    // "Payment method added" banner (loadBillingInformationPageSaga dispatches this reset as the
    // first action of the page load). The banner is still consumed exactly once via the mount-
    // effect clearRecentlyAddedPaymentMethod dispatch inside SavedPaymentMethods.
    case types.BILLING_INFO_RESET_PAGE: return {
      ...initState,
      recentlyAddedMessage: state.recentlyAddedMessage,
    };
    // #genai Phase 2: clear the form slices the load saga hydrated (card / ACH / address + their
    // error slices) so /add-card and /add-ach always render a fresh, empty form. Saved data is
    // shown on the /billing-information list page, not pre-populated into the add forms.
    //
    // #genai Also reset submitInfo here (Cursor Bugbot: "Stale submit state add routes"). Without
    // this, a prior save's showMessage/errorMessage can linger in Redux across mounts of the add
    // pages — the AddAch / AddCard success useEffect would fire immediately on mount and bounce the
    // user straight back to /billing-information, or the error dialog would open before the user
    // even submits. Every mount of these dedicated add routes should start with a clean submit slice.
    case types.BILLING_INFO_RESET_ADD_PAYMENT_FORM: return {
      ...state,
      submitInfo: { ...initState.submitInfo },
      cardPaymentInfo: { ...cardPaymentInfoInit, isUpdated: false, isCardUpdated: false },
      achPaymentInfo: { ...achPaymentInfoInit, isUpdated: false },
      // #genai Phase 2 add-payment routes open a fresh form with NO billing GET, so clear the
      // saved-bank summary that persists in Redux from the /billing-information visit — otherwise
      // AchPaymentInformation renders it in summary mode (masked routing/account suffix). Submit
      // still resolves billPayerId from userData.billingInformation (billingInformationSnapshot).
      achInfo: {},
      // #genai Also wipe the consent flags that would otherwise carry over from the prior
      // /billing-information visit — without this, the ACH "I have read and agreed to Terms &
      // Conditions" or Card "Save your credit card" checkbox can render pre-checked on a route
      // that's supposed to be a fresh form. The Card page re-dispatches updateCreditCardConsent(true)
      // on mount per Figma 269:21589; the ACH page leaves achConsent false per Figma 269:21702.
      ccConsent: false,
      updatedCCConsent: false,
      achConsent: false,
      updatedACHConsent: false,
      isACHDefault: false,
      updatedACHDefault: false,
      addressInfo: { ...addressInfoInit },
      isAddressInfoUpdated: false,
      cardPaymentInfoErrors: { ...cardPaymentInfoErrorsInit },
      cardPaymentInfoErrorsForBanner: { ...cardPaymentInfoErrorsInit },
      achPaymentInfoErrors: { ...achPaymentInfoErrorsInit },
      achPaymentInfoErrorsForBanner: { ...achPaymentInfoErrorsInit },
      addressInfoErrors: { ...addressInfoInit },
      addressInfoErrorsForBanner: { ...addressInfoInit },
      addressInfoErrorsForDisplay: { ...addressInfoInit },
      addressInfoErrorsForBannerDisplay: { ...addressInfoInit },
    };
    case types.PAYMENT_TOKEN_RESET: return { ...state, cardData: null };
    case types.PAYMENT_TOKEN_SUCCESS: return { ...state, cardData: action.cardData, ccConsent: action.ccConsent };
    case types.PAYMENT_TOKEN_ERROR: return { ...state, cardData: null };
    case types.BILLING_INFO_SUBMIT_START: return { ...state, isLoading: true };
    case types.BILLING_INFO_SUBMIT_RESET: return { ...state, submitInfo: { ...initState.submitInfo } };
    case types.BILLING_INFO_SUBMIT_COMPLETE: return {
      ...state,
      isLoading: false,
      updatedCCConsent: false,
      updatedACHConsent: false, // #genai
      updatedACHDefault: false, // #genai
      isSaveDisclaimerOpen: false, // #genai
      savedSelectedPaymentMethod: state.selectedPaymentMethod, // #genai
      submitInfo: {
        ...state.submitInfo, showMessage: true, errorMessage: null, errorCode: null, errorType: null, supportReference: null,
      },
    };
    case types.BILLING_INFO_SUBMIT_ERROR: return {
      ...state,
      isLoading: false,
      submitInfo: {
        ...state.submitInfo,
        showMessage: true,
        errorMessage: action.errorMessage,
        errorCode: action.errorCode,
        errorType: action.errorType || null,
        supportReference: action.supportReference || null,
      },
    };
    case types.BILLING_INFO_SUBMIT_VALIDATION_ERROR: return {
      ...state,
      isLoading: false,
    };
    // #genai Phase 2 — load unified payment methods list.
    case types.LOAD_PAYMENT_METHODS_START: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods, loading: true, loadError: null,
      },
    };
    case types.LOAD_PAYMENT_METHODS_SUCCESS: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        byId: toPaymentMethodsById(action.paymentMethods),
        loading: false,
        loaded: true,
        loadError: null,
      },
    };
    case types.LOAD_PAYMENT_METHODS_ERROR: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods, loading: false, loaded: true, loadError: action.loadError || null,
      },
    };
    // #genai Phase 2 — optimistic set-default: flip target row true, clear EVERY other default
    // across the whole list (both credit-card AND ach rows). Mirrors the backend contract from
    // guide §12.4 + top-of-file rule: "The server flips the chosen row to IsDefault=1 and clears
    // every sibling row in the same scope to 0 in one SQL transaction. For ACH defaults it also
    // keeps BillPayer.IsACHDefault consistent." At most one row across the whole list may be
    // isDefault=true, so the optimistic update must be scoped the same way — otherwise two
    // DEFAULT badges can coexist while the server is still processing.
    case types.SET_PAYMENT_METHOD_DEFAULT: {
      const { type, id } = action.method;
      const key = paymentMethodKey(type, id);
      const previousById = state.paymentMethods.byId;
      const nextById = Object.keys(previousById).reduce((acc, rowKey) => {
        acc[rowKey] = { ...previousById[rowKey], isDefault: rowKey === key };
        return acc;
      }, {});
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          byId: nextById,
          previousById,
          pendingActionKey: key,
          actionError: null,
        },
      };
    }
    case types.SET_PAYMENT_METHOD_DEFAULT_SUCCESS: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods, previousById: null, pendingActionKey: null,
      },
    };
    case types.SET_PAYMENT_METHOD_DEFAULT_ERROR: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        byId: state.paymentMethods.previousById || state.paymentMethods.byId,
        previousById: null,
        pendingActionKey: null,
        actionError: { ...action.actionError, kind: PAYMENT_METHODS_ACTION_ERROR_KIND.Default, method: action.method },
      },
    };
    // #genai Phase 2 — remove is confirmed-then-applied, NOT optimistic. Dropping the row up front
    // emptied the list while the request was in flight, which made the page swap to BillingEmptyState
    // and back when the last method's remove was rejected (409 CannotRemoveDefault / 500). Marking the
    // row pending instead keeps the list stable; `isPending` already disables its actions meanwhile.
    case types.REMOVE_PAYMENT_METHOD: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        // #genai No snapshot: nothing was mutated, so the failure paths have nothing to roll back.
        previousById: null,
        pendingActionKey: paymentMethodKey(action.method.type, action.method.id),
        actionError: null,
      },
    };
    // #genai The row is deleted only once the server confirms it.
    case types.REMOVE_PAYMENT_METHOD_SUCCESS: {
      const nextById = { ...state.paymentMethods.byId };
      delete nextById[paymentMethodKey(action.method.type, action.method.id)];
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods, byId: nextById, previousById: null, pendingActionKey: null,
        },
      };
    }
    // #genai Failure paths leave `byId` untouched — the row was never removed. They must NOT restore
    // `previousById`: that snapshot belongs to an in-flight SET_PAYMENT_METHOD_DEFAULT, and reinstating
    // it here would silently revert the user's default change.
    case types.REMOVE_PAYMENT_METHOD_ERROR: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        pendingActionKey: null,
        actionError: { ...action.actionError, kind: PAYMENT_METHODS_ACTION_ERROR_KIND.Remove, method: action.method },
      },
    };
    // #genai Phase 2 — 409 CannotRemoveDefault: surface the blocked state (list already intact).
    case types.REMOVE_PAYMENT_METHOD_BLOCKED_DEFAULT: return {
      ...state,
      paymentMethods: {
        ...state.paymentMethods,
        pendingActionKey: null,
        actionError: { errorType: 'CannotRemoveDefault', kind: PAYMENT_METHODS_ACTION_ERROR_KIND.Blocked, method: action.method },
      },
    };
    case types.RESET_PAYMENT_METHODS_ACTION_ERROR: return {
      ...state,
      paymentMethods: { ...state.paymentMethods, actionError: null },
    };
    // #genai Phase 2 — success-banner handoff between Add-payment routes and the saved-methods list.
    case types.SET_RECENTLY_ADDED_PAYMENT_METHOD: return {
      ...state,
      recentlyAddedMessage: action.message || null,
    };
    case types.CLEAR_RECENTLY_ADDED_PAYMENT_METHOD: return {
      ...state,
      recentlyAddedMessage: null,
    };
    default: return state;
  }
};

export default billingInformationReducer;
